import { useState, useEffect } from 'react';
import { Auth } from '~/amplify';
import possibleTypes from '~/graphql/possibleTypes.json';
import { ApolloClient, InMemoryCache, ApolloLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import isSubscriptionOperation from './utils/isSubscriptionOperation';
import createSubscriptionWebsocketLink from '../../CognitoOpenID/createSubscriptionWebsocketLink';
import createHttpLink from '../../CognitoOpenID/createHttpLink';
import { navigate, useLocation } from '@gatsbyjs/reach-router';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import {
  mergeInfinitePaginatedItems,
  readInfinitePaginatedItems,
} from './utils/pagination';
import { reporter } from '~/hooks/useErrorReporter';
import isUnauthorizedPath from './utils/isUnauthorizedPath';
import errorHandler from './utils/errorHandler';

const useDHApolloClient = () => {
  const [client, setClient] = useState<ApolloClient<any> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const createApolloClient = async () => {
      const region = process.env.AWS_REGION;
      const appSyncApiUrl = process.env.API_ENDPOINT;

      if (!appSyncApiUrl || !region) {
        throw Error(`Some environment variables are missing, you probably forgot to make your .env file.
            API_ENDPOINT=${appSyncApiUrl}
            AWS_REGION=${region}`);
      }

      const getJWTToken = async () => {
        try {
          const session = await Auth.fetchAuthSession();
          return session.tokens?.accessToken?.toString();
        } catch (error) {
          if (isExpiredOrNotAuthorizedSessionError(error)) {
            void navigate('/logout');
            return;
          }
          if (
            error &&
            typeof error === 'string' &&
            error.includes('No current user')
          ) {
            if (isUnauthorizedPath(location.pathname)) {
              void navigate(`${location.pathname}${location.search}`, {
                state: {
                  ...(location.state ?? {}),
                  from: location.pathname,
                },
              });
            }

            void navigate(`/login${location.search}`, {
              state: {
                ...(location.state ?? {}),
                from: location.pathname,
              },
            });
            return;
          }
        }

        return;
      };

      const wsLink = await createSubscriptionWebsocketLink({
        appSyncApiUrl,
        getJWTToken,
      });

      if (!wsLink) {
        reporter.captureException(
          new Error('Unable to create WebSocket Link for Apollo client'),
        );
        return;
      }

      const subscriptionLink = ApolloLink.split(
        isSubscriptionOperation,
        wsLink,
        createHttpLink({ appSyncApiUrl, getJWTToken }),
      );

      const errorLink = onError(errorHandler);
      const link = from([errorLink, subscriptionLink]);

      try {
        const client = new ApolloClient({
          defaultOptions: {
            mutate: {
              errorPolicy: 'all',
            },
            query: {
              errorPolicy: 'all',
              fetchPolicy: 'network-only',
            },
            watchQuery: {
              errorPolicy: 'all',
              fetchPolicy: 'cache-and-network',
              nextFetchPolicy: 'cache-first',
            },
          },
          link,
          cache: new InMemoryCache({
            possibleTypes,
            typePolicies: {
              Flow___InstanceCondition: {
                keyFields: false,
              },
              ContactFiltersFlattened: {
                keyFields: false,
              },
              Flow___SubjectFieldCondition: {
                keyFields: false,
              },
              ContactWithLastActivity: {
                keyFields: false,
              },
              Query: {
                fields: {
                  getInvoices: {
                    keyArgs: false,
                    merge: mergeInfinitePaginatedItems,
                    read: readInfinitePaginatedItems('PaginatedInvoices'),
                  },
                  getCreditInvoices: {
                    keyArgs: false,
                    merge: mergeInfinitePaginatedItems,
                    read: readInfinitePaginatedItems('PaginatedInvoices'),
                  },
                },
              },
            },
          }),
        });
        setClient(client);
      } catch (error) {
        reporter.captureException(error);
      }
    };

    void createApolloClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return client;
};

export default useDHApolloClient;
